import { EligibilityClarificationCode, OtherCoverageCode } from "@/models/PatientPlan";

export interface DurPpsOverride {
    reasonForServiceCode: string;
    professionalServiceCode: string;
    resultOfServiceCode: string;
    levelOfEffort: string;
}

/**
 * The per-claim deviations a technician can apply to a bill. Held as one object rather than a
 * spread of sibling fields so the same set can be filled in for an immediate bill and for a
 * scheduled one without the two forms drifting apart.
 *
 * The scalar fields are typed the way the inputs bind them — strings for the free-text and money
 * boxes — so this is the shape the billing query string is built from directly.
 *
 * The two code fields are the exception: EnumSelector hands back the numeric enum member, so they
 * hold a number once one has been picked and the empty string until then. Typed for both, because
 * declaring them string alone is what let a consumer call a string method on a number.
 */
export class BillingOverrides {
    paTypeCode: string | number = "";
    paOverride: string = "";
    tsn: string = "";
    submissionClarificationCode: string | number = "";
    eligibilityClarificationCodeOverride: EligibilityClarificationCode | null = null;
    uacOverride: string = "";
    incentiveAmountOverride: string = "";
    icd10: string = "";
    dateOfInjuryOverride: string = "";
    otherCoverageCodeOverride: OtherCoverageCode | null = null;
    taxPercentageOverride: number | null = null;
    rejectCodeOverride: string | null = null;
    daysSupplyOverride: number | null = null;
    durPpsOverrides: DurPpsOverride[] = [];

    constructor(source: Partial<BillingOverrides> | null = null) {
        if (source) Object.assign(this, source);
        // Assign never replaces an absent array with the field initializer, so a partial source
        // that omits the DUR rows would otherwise leave the shared default array aliased.
        if (!Array.isArray(this.durPpsOverrides)) this.durPpsOverrides = [];
    }

    static emptyDur(): DurPpsOverride {
        return {
            reasonForServiceCode: "",
            professionalServiceCode: "",
            resultOfServiceCode: "",
            levelOfEffort: "",
        };
    }

    /**
     * True when the technician has entered a deviation, used to keep a filled-in form open.
     *
     * Named for the technician rather than for the fields because it deliberately ignores two of them:
     * dateOfInjuryOverride and eligibilityClarificationCodeOverride are seeded from the selected plan on
     * every plan change and every fetch, so counting them would read "deviated from the plan" for any
     * plan that merely stores one — every workers' comp plan with a Date of Injury would open the
     * override panel expanded, and resetOverrides() could never collapse it, because it re-seeds both
     * immediately after clearing. A value typed into either box is still transmitted; it just does not
     * open a panel the technician had to open to type it in.
     */
    get hasTechnicianEntry(): boolean {
        return !!(this.paTypeCode || this.paOverride || this.tsn || this.submissionClarificationCode
            || this.uacOverride || this.incentiveAmountOverride || this.icd10
            || this.otherCoverageCodeOverride != null || this.taxPercentageOverride != null
            || this.rejectCodeOverride || this.daysSupplyOverride != null
            || this.durPpsOverrides.length > 0);
    }
}
